// AtlasPPC bridge — the single client-side seam through which OS.js applications
// reach platform data (osjs-004-application-contract). The browser never imports
// Kafka/InfluxDB/Governor; it calls core.make('osjs/atlasppc-bridge').fetch(kind,
// query), which POSTs to the OS.js server's /atlasppc/:kind route.
//
// `kind` is an OPAQUE string to the client (e.g. 'devices'). The client never
// knows what `kind` resolves to on the server. The browser talks ONLY to this
// server-side bridge; the OS.js SERVER calls the platform backend.
//
// Returns {ok:true, data} on success, {ok:false, error} on failure (server error
// envelope OR a network failure — both surfaced honestly, never silently).
//
// Uses the standard browser fetch API (same-origin → sends the OS.js session
// cookie). ING-011: re-created from the osjs-004 proven prototype (the source was
// absent at HEAD — ING-001).

import createLiveInterest from './live-interest.mjs';

export default class AtlasPPCBridgeServiceProvider {
  constructor(core) {
    this.core = core;
  }

  provides() {
    return ['osjs/atlasppc-bridge'];
  }

  init() {
    // UI-PS-003 — DSM subscribe rides the Ingestor realtime raw topic.
    // Callback shape stays {id, plugin:{frames}}|{id, error}.
    const subscribers = new Map();
    const pointSubscribers = new Map();
    const statusSubscribers = new Map();
    const enrollmentSubscribers = new Map();
    const rawSubscribers = new Map();
    this.core.on('atlasppc/realtime', (payload) => {
      if (!payload || typeof payload.tag !== 'string') return;
      let map = null;
      if (typeof payload.status === 'string') map = statusSubscribers;
      else if (payload.enrollment) map = enrollmentSubscribers;
      else if (Array.isArray(payload.frames)) map = rawSubscribers;
      else if (payload.values) map = pointSubscribers;
      else if (payload.error) map = [statusSubscribers, pointSubscribers, enrollmentSubscribers, rawSubscribers];
      const maps = Array.isArray(map) ? map : (map ? [map] : []);
      maps.forEach((m) => {
        const set = m.get(payload.tag);
        if (!set) return;
        set.forEach((cb) => {
          try { cb(payload); } catch (e) { console.warn('atlasppc-bridge realtime subscriber error', e); }
        });
      });
    });
    const transportListeners = new Set();
    this.core.on('atlasppc/transport', (payload) => {
      if (!payload || typeof payload.connected !== 'boolean') return;
      transportListeners.forEach((cb) => {
        try { cb(payload.connected, payload.error || ''); } catch (e) {
          console.warn('atlasppc-bridge transport subscriber error', e);
        }
      });
    });
    const subscribePoint = (tag, cb) => {
      const key = String(tag);
      let set = pointSubscribers.get(key);
      if (!set) {
        set = new Set();
        pointSubscribers.set(key, set);
        this.core.send('atlasppc/realtime-subscribe', key);
      }
      set.add(cb);
      return () => {
        set.delete(cb);
        if (!set.size) {
          pointSubscribers.delete(key);
          this.core.send('atlasppc/realtime-unsubscribe', key);
        }
      };
    };
    this.core.singleton('osjs/atlasppc-bridge', () => ({
      fetch: (kind, query) => this.fetch(kind, query),
      subscribe: (id, cb) => {
        const key = String(id);
        const wrapped = (payload) => {
          if (!payload) return;
          if (Array.isArray(payload.frames)) {
            cb({id: payload.tag || key, plugin: {frames: payload.frames}});
            return;
          }
          if (payload.error) {
            const raw = String(payload.error);
            const transport = /live transport|realtime upstream|not configured/i.test(raw)
              ? 'live transport down'
              : raw;
            cb({id: payload.tag || key, error: transport});
          }
        };
        let set = rawSubscribers.get(key);
        if (!set) {
          set = new Set();
          rawSubscribers.set(key, set);
          this.core.send('atlasppc/realtime-subscribe-raw', key);
        }
        set.add(wrapped);
        subscribers.set(key, set);
        return () => {
          set.delete(wrapped);
          if (!set.size) {
            rawSubscribers.delete(key);
            subscribers.delete(key);
            this.core.send('atlasppc/realtime-unsubscribe-raw', key);
          }
        };
      },
      subscribePoint,
      createPointInterest: (cb) => createLiveInterest(subscribePoint, cb),
      subscribeDeviceStatus: (id, cb) => {
        const key = String(id);
        let set = statusSubscribers.get(key);
        if (!set) {
          set = new Set();
          statusSubscribers.set(key, set);
          this.core.send('atlasppc/realtime-subscribe-status', key);
        }
        set.add(cb);
        return () => {
          set.delete(cb);
          if (!set.size) {
            statusSubscribers.delete(key);
            this.core.send('atlasppc/realtime-unsubscribe-status', key);
          }
        };
      },
      subscribeDeviceEnrollment: (id, cb) => {
        const key = String(id);
        let set = enrollmentSubscribers.get(key);
        if (!set) {
          set = new Set();
          enrollmentSubscribers.set(key, set);
          this.core.send('atlasppc/realtime-subscribe-enrollment', key);
        }
        set.add(cb);
        return () => {
          set.delete(cb);
          if (!set.size) {
            enrollmentSubscribers.delete(key);
            this.core.send('atlasppc/realtime-unsubscribe-enrollment', key);
          }
        };
      },
      subscribeTransportState: (cb) => {
        transportListeners.add(cb);
        return () => { transportListeners.delete(cb); };
      }
    }));
    return Promise.resolve();
  }

  start() { return Promise.resolve(); }

  destroy() {}

  fetch(kind, query = {}) {
    return fetch(`/atlasppc/${encodeURIComponent(kind)}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(query || {})
    })
      .then((res) => res.json())
      .then((envelope) => envelope || {ok: false, error: 'empty response'})
      .catch((err) => ({ok: false, error: (err && err.message) || 'network error'}));
  }
}
